/* =============================================================================
   CORE / LOTS — Chargen / MHD (Phase E6a, S165)
   =============================================================================
   PURE Helfer für die Chargen-Sicht (stock_lots, Migration phase_124). MHD-Alarm
   zweistufig: abgelaufen (mhd < heute) · läuft bald ab (mhd <= heute + Vorlauf-Tage).
   Single-Source bleibt der stock_movements-Ledger; Chargen sind die feinere Überlagerung.

   E6a-Grenze (ehrlich): remaining_base sinkt nur durch manuelle Ausbuchung, nicht durch
   Verkäufe — das löst E6b (FEFO) auf.
============================================================================ */

import { AMPEL_COLORS, formatEuro } from './food-cost.js';

const DAY_MS = 86400000;

// ----------------------------------------------------------------------------
// S203 Cut B — Chargen-genaue Bewertung (phase_152 stock_lots.unit_cost_cents)
// ----------------------------------------------------------------------------
// unit_cost_cents = EK pro Basiseinheit in Cent (sub-cent-genau, NUMERIC). Der
// Charge-Wert ist remaining_base × unit_cost_cents (Cent). Die RPCs lot_overview/
// lots_for liefern value_cents bereits vorberechnet; diese Helfer spiegeln das
// clientseitig (Tests/Anzeige/Summe) und formatieren €.

/** Charge-Wert in Cent (remaining_base × EK/Basiseinheit). null, wenn unbewertet. */
export function lotValueCents(remainingBase, unitCostCents) {
  const r = Number(remainingBase), c = Number(unitCostCents);
  if (unitCostCents == null || !isFinite(r) || !isFinite(c)) return null;
  return r * c;
}

/** Cent → „12,50 €" (über formatEuro). null/NaN → „—" (nicht 0 €). */
export function formatCentsEuro(cents) {
  if (cents == null || !isFinite(Number(cents))) return '—';
  return formatEuro(Number(cents) / 100);
}

/** Σ Charge-Wert (value_cents) über Chargen-Zeilen; unbewertete (null) zählen 0. Cent. */
export function sumLotValueCents(lots) {
  return (Array.isArray(lots) ? lots : []).reduce((s, l) => {
    const v = l && l.valueCents;
    return s + (v != null && isFinite(Number(v)) ? Number(v) : 0);
  }, 0);
}

/** Anzahl Chargen ohne EK (value_cents/unitCostCents == null) → „nicht bewertet". */
export function countUnvaluedLots(lots) {
  return (Array.isArray(lots) ? lots : []).filter(l => !l || l.unitCostCents == null).length;
}

function toUTCDay(d) {
  if (d == null) return null;
  if (d instanceof Date) return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  if (!y || !m || !day) return null;
  return Date.UTC(y, m - 1, day);
}

/** Tage bis MHD relativ zum Referenzdatum (mhd − ref); null ohne MHD. Negativ = abgelaufen. */
export function daysUntil(mhd, ref) {
  const a = toUTCDay(mhd), b = toUTCDay(ref);
  if (a == null || b == null) return null;
  return Math.round((a - b) / DAY_MS);
}

/**
 * Alarm-Stufe einer Charge: 'none' (kein MHD) · 'expired' (mhd < heute) ·
 * 'near' (0..Vorlauf Tage) · 'ok'. Spiegel der SQL-Flags is_expired/is_near_expiry.
 */
export function lotAlarmLevel(mhd, ref, vorlaufTage) {
  if (mhd == null) return 'none';
  const d = daysUntil(mhd, ref);
  if (d == null) return 'none';
  if (d < 0) return 'expired';
  if (d <= (Number(vorlaufTage) || 0)) return 'near';
  return 'ok';
}

/** Farbe je Alarm-Stufe (rot/gelb/grün/grau) — zentrale AMPEL_COLORS (S166). */
export function lotColor(level) {
  return ({ expired: AMPEL_COLORS.red, near: AMPEL_COLORS.yellow, ok: AMPEL_COLORS.green, none: AMPEL_COLORS.unknown })[level]
    || AMPEL_COLORS.unknown;
}

/** Chargen nach MHD aufsteigend sortieren, MHD-lose zuletzt (FEFO-Reihenfolge). Reine Funktion. */
export function sortLotsByMhd(lots) {
  return (Array.isArray(lots) ? [...lots] : []).sort((a, b) => {
    if (!a || !a.mhd) return (!b || !b.mhd) ? 0 : 1;
    if (!b || !b.mhd) return -1;
    return a.mhd < b.mhd ? -1 : (a.mhd > b.mhd ? 1 : 0);
  });
}

/** Zähler {expired, near} über Chargen relativ zum Vorlauf (für die Alarm-Banner). */
export function countByAlarm(lots, ref, vorlaufTage) {
  let expired = 0, near = 0;
  for (const l of (Array.isArray(lots) ? lots : [])) {
    const lvl = lotAlarmLevel(l && l.mhd, ref, vorlaufTage);
    if (lvl === 'expired') expired++;
    else if (lvl === 'near') near++;
  }
  return { expired, near };
}

/** Ausbuchungsmenge einer Charge (= Restbestand, der als Schwund gebucht wird). */
export function writeOffQty(lot) {
  const r = lot && Number(lot.remainingBase);
  return isFinite(r) && r > 0 ? r : 0;
}
